/**
 * AugmentationSelectionModal Component (CSS Theme)
 * Uses CSS classes from chess-components.css instead of inline styles
 */

import type {
  Augmentation,
  AugmentationCategory,
} from "@cloudnest/augmented-chess-engine";
import {
  getAugmentation,
  getColorDisplayName,
  getPieceTypeDisplayName,
} from "@cloudnest/augmented-chess-engine";
import { useEffect, useMemo, useState } from "react";
import type { AugmentationSelectionModalProps } from "../../types";

const EMPTY_IDS: readonly string[] = [];

const CATEGORY_LABELS: Record<AugmentationCategory, string> = {
  MOVEMENT_MODIFIER: "이동 변경",
  PHASE_MOVEMENT: "관통 이동",
  PROMOTION_MODIFIER: "승진 변경",
  MOVEMENT_SWAP: "패턴 교환",
  WIN_CONDITION_MODIFIER: "승리 조건",
  CAPTURE_MODIFIER: "포획 변형",
  RESOURCE: "자원",
  DEFENSE: "방어",
};

interface AugmentationCardProps {
  augmentation: Augmentation;
  onSelect: () => void;
  isSelected: boolean;
  disabled: boolean;
}

function AugmentationCard({
  augmentation,
  onSelect,
  isSelected,
  disabled,
}: AugmentationCardProps): JSX.Element {
  const cardClasses = isSelected
    ? "chess-aug-card chess-aug-card-selected"
    : "chess-aug-card";

  return (
    <button
      type="button"
      data-testid="aug-card"
      onClick={disabled ? undefined : onSelect}
      className={cardClasses}
      disabled={disabled}
    >
      <h3 className="chess-aug-card-title">
        {augmentation.nameKo ?? augmentation.name}
      </h3>
      <p className="chess-aug-card-description">
        {augmentation.descriptionKo ?? augmentation.description}
      </p>
      <span className="chess-aug-card-pieces">
        적용 대상:{" "}
        {augmentation.targetPieceTypes.map(getPieceTypeDisplayName).join(", ")}
      </span>
    </button>
  );
}

function getAugmentationDetailLines(augmentation: Augmentation): string[] {
  switch (augmentation.effect.type) {
    case "ADD_PATTERN":
      if (augmentation.effect.pattern === "KING_STEP") {
        return ["대상 기물에 킹 이동(전후좌우+대각선 1칸) 패턴을 추가합니다."];
      }
      if (augmentation.effect.pattern === "DIAGONAL_2") {
        return ["대상 기물에 대각선 2칸 이동 패턴을 추가합니다."];
      }
      if (augmentation.effect.pattern === "FORWARD_2") {
        return [
          "대상 폰은 시작 위치 여부와 무관하게 전방 2칸 이동 패턴을 추가로 사용합니다.",
        ];
      }
      if (augmentation.effect.pattern === "ORTHOGONAL_2") {
        return ["대상 기물에 직선(가로/세로) 2칸 이동 패턴을 추가합니다."];
      }
      return ["대상 기물의 이동 패턴을 확장합니다."];
    case "EXTEND_RANGE":
      return [
        `슬라이딩 기물의 이동 가능 거리를 ${augmentation.effect.rangeBonus ?? 0}칸 늘립니다.`,
      ];
    case "PHASE_MOVEMENT":
      return [
        "대상 기물은 다른 기물을 통과해 이동할 수 있습니다.",
        "착지 칸은 규칙에 맞는 빈 칸 또는 상대 기물 칸이어야 합니다.",
      ];
    case "PROMOTION_RANK_OVERRIDE":
      return [
        `백 폰은 ${augmentation.effect.whiteRank}랭크, 흑 폰은 ${augmentation.effect.blackRank}랭크에서도 승진할 수 있습니다.`,
      ];
    case "MOVEMENT_SWAP":
      return (augmentation.effect.swapPairs ?? []).map(
        (pair) =>
          `${getPieceTypeDisplayName(pair.pieceA)} ↔ ${getPieceTypeDisplayName(pair.pieceB)} 이동 패턴을 교환합니다.`,
      );
    case "CONSECUTIVE_CHECK_WIN":
      return [
        `같은 플레이어가 연속 ${augmentation.effect.checkCount}회 체크하면 즉시 승리합니다.`,
        "중간에 체크가 아닌 수를 두면 연속 체크 카운트가 초기화됩니다.",
      ];
    default:
      return ["게임 규칙을 변경하는 효과가 적용됩니다."];
  }
}

interface AugmentationDetailDialogProps {
  augmentation: Augmentation;
  onClose: () => void;
}

function AugmentationDetailDialog({
  augmentation,
  onClose,
}: AugmentationDetailDialogProps): JSX.Element {
  const detailLines = getAugmentationDetailLines(augmentation);

  return (
    <div className="chess-modal-overlay" onClick={onClose}>
      <div
        className="chess-modal chess-aug-detail-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="chess-modal-header">
          <h2>{augmentation.nameKo ?? augmentation.name}</h2>
          <p>{CATEGORY_LABELS[augmentation.category]}</p>
        </div>

        <div className="chess-aug-detail-section">
          <h3 className="chess-aug-detail-title">기본 설명</h3>
          <p className="chess-aug-detail-text">
            {augmentation.descriptionKo ?? augmentation.description}
          </p>
        </div>

        <div className="chess-aug-detail-section">
          <h3 className="chess-aug-detail-title">적용 대상</h3>
          <p className="chess-aug-detail-text">
            {augmentation.targetPieceTypes.length > 0
              ? augmentation.targetPieceTypes
                  .map(getPieceTypeDisplayName)
                  .join(", ")
              : "전체 규칙"}
          </p>
        </div>

        <div className="chess-aug-detail-section">
          <h3 className="chess-aug-detail-title">효과 상세</h3>
          <ul className="chess-aug-detail-list">
            {detailLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>

        <div className="chess-aug-detail-actions">
          <button
            type="button"
            className="chess-detail-button"
            onClick={onClose}
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

export function AugmentationSelectionModalThemed({
  gameState,
  augmentationChoices,
  selectingPlayer,
  onSelectAugmentation,
}: AugmentationSelectionModalProps): JSX.Element | null {
  // All hooks must be called before any early return (Rules of Hooks)

  // Get pending selections (safe regardless of phase)
  const whitePending = gameState.augmentations?.white?.pendingSelection ?? null;
  const blackPending = gameState.augmentations?.black?.pendingSelection ?? null;

  // Stable reference when undefined to avoid defeating useMemo
  const whiteActive = gameState.augmentations?.white?.active ?? EMPTY_IDS;
  const blackActive = gameState.augmentations?.black?.active ?? EMPTY_IDS;

  const whiteActiveAugmentations = useMemo(
    () =>
      whiteActive
        .map((id) => getAugmentation(id))
        .filter((aug): aug is Augmentation => aug !== undefined),
    [whiteActive],
  );

  const blackActiveAugmentations = useMemo(
    () =>
      blackActive
        .map((id) => getAugmentation(id))
        .filter((aug): aug is Augmentation => aug !== undefined),
    [blackActive],
  );

  const currentPendingSelection =
    selectingPlayer === "WHITE"
      ? whitePending
      : selectingPlayer === "BLACK"
        ? blackPending
        : null;

  const [stagedSelectionId, setStagedSelectionId] = useState<string | null>(
    currentPendingSelection ?? null,
  );
  const [detailAugmentation, setDetailAugmentation] =
    useState<Augmentation | null>(null);

  useEffect(() => {
    setStagedSelectionId(currentPendingSelection ?? null);
  }, [currentPendingSelection]);

  useEffect(() => {
    if (!detailAugmentation) return;
    if (stagedSelectionId === detailAugmentation.id) return;
    setDetailAugmentation(null);
  }, [detailAugmentation, stagedSelectionId]);

  // Don't show if not in augmentation selection phase
  if (
    gameState.phase !== "AUGMENTATION_SELECTION" ||
    !gameState.augmentationSelection
  ) {
    return null;
  }

  const { whiteSelected, blackSelected } = gameState.augmentationSelection;

  const stagedSelection = stagedSelectionId
    ? (getAugmentation(stagedSelectionId) ?? null)
    : null;

  const canConfirmSelection = Boolean(selectingPlayer && stagedSelectionId);

  const handleConfirmSelection = () => {
    if (!selectingPlayer || !stagedSelectionId) return;
    onSelectAugmentation(stagedSelectionId, selectingPlayer);
  };

  const indicatorClass =
    selectingPlayer === "WHITE"
      ? "chess-player-indicator chess-player-indicator-white"
      : "chess-player-indicator chess-player-indicator-black";

  return (
    <div data-testid="aug-modal-overlay" className="chess-modal-overlay">
      <div className="chess-modal">
        <div className="chess-modal-header">
          <h2 data-testid="aug-modal-title">증강 선택</h2>
          <p>선택한 증강은 매 라운드마다 누적되어 적용됩니다</p>
        </div>

        <div className="chess-status">
          <span
            className={
              whiteSelected ? "chess-status-success" : "chess-status-pending"
            }
          >
            백:{" "}
            {whiteSelected
              ? "선택 완료"
              : selectingPlayer === "WHITE"
                ? "선택 중..."
                : "대기 중..."}
          </span>
          {" | "}
          <span
            className={
              blackSelected ? "chess-status-success" : "chess-status-pending"
            }
          >
            흑:{" "}
            {blackSelected
              ? "선택 완료"
              : selectingPlayer === "BLACK"
                ? "선택 중..."
                : "대기 중..."}
          </span>
        </div>

        <div className="chess-modal-note">
          같은 플레이어가 다시 증강을 고를 때 기존 증강 위에 추가로 누적됩니다.
        </div>

        <div className="chess-aug-progress-grid">
          <div className="chess-aug-progress-card">
            <div className="chess-aug-progress-header">
              <span className="chess-aug-progress-title">백 누적 증강</span>
              <span className="chess-aug-progress-count">
                {whiteActiveAugmentations.length}개
              </span>
            </div>
            {whiteActiveAugmentations.length > 0 ? (
              <ul className="chess-aug-progress-list">
                {whiteActiveAugmentations.map((aug) => (
                  <li key={aug.id} className="chess-aug-progress-item">
                    {aug.nameKo ?? aug.name}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="chess-aug-progress-empty">
                아직 누적된 증강이 없습니다
              </p>
            )}
            {selectingPlayer === "WHITE" && stagedSelection && (
              <p className="chess-aug-progress-preview">
                이번 선택 예정: {stagedSelection.nameKo ?? stagedSelection.name}
              </p>
            )}
            {whiteSelected && selectingPlayer !== "WHITE" && (
              <p className="chess-aug-progress-preview">
                이번 라운드 선택 완료 (상대 공개 대기)
              </p>
            )}
          </div>

          <div className="chess-aug-progress-card">
            <div className="chess-aug-progress-header">
              <span className="chess-aug-progress-title">흑 누적 증강</span>
              <span className="chess-aug-progress-count">
                {blackActiveAugmentations.length}개
              </span>
            </div>
            {blackActiveAugmentations.length > 0 ? (
              <ul className="chess-aug-progress-list">
                {blackActiveAugmentations.map((aug) => (
                  <li key={aug.id} className="chess-aug-progress-item">
                    {aug.nameKo ?? aug.name}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="chess-aug-progress-empty">
                아직 누적된 증강이 없습니다
              </p>
            )}
            {selectingPlayer === "BLACK" && stagedSelection && (
              <p className="chess-aug-progress-preview">
                이번 선택 예정: {stagedSelection.nameKo ?? stagedSelection.name}
              </p>
            )}
            {blackSelected && selectingPlayer !== "BLACK" && (
              <p className="chess-aug-progress-preview">
                이번 라운드 선택 완료 (상대 공개 대기)
              </p>
            )}
          </div>
        </div>

        {selectingPlayer && (
          <div className={indicatorClass}>
            {getColorDisplayName(selectingPlayer)} 선택 차례
          </div>
        )}

        <div className="chess-aug-choices">
          {augmentationChoices.map((aug) => (
            <AugmentationCard
              key={aug.id}
              augmentation={aug}
              onSelect={() => setStagedSelectionId(aug.id)}
              isSelected={stagedSelectionId === aug.id}
              disabled={!selectingPlayer}
            />
          ))}
        </div>

        <div className="chess-aug-actions">
          <p className="chess-aug-selected-preview">
            {stagedSelection
              ? `선택 예정: ${stagedSelection.nameKo ?? stagedSelection.name}`
              : "먼저 카드 하나를 선택하세요"}
          </p>
          <div className="chess-aug-action-buttons">
            <button
              type="button"
              className="chess-detail-button"
              disabled={!stagedSelection}
              onClick={() => {
                if (!stagedSelection) return;
                setDetailAugmentation(stagedSelection);
              }}
            >
              상세 보기
            </button>
            <button
              type="button"
              data-testid="aug-confirm-btn"
              className="chess-approve-button"
              disabled={!canConfirmSelection}
              onClick={handleConfirmSelection}
            >
              선택 승인
            </button>
          </div>
        </div>
      </div>

      {detailAugmentation && (
        <AugmentationDetailDialog
          augmentation={detailAugmentation}
          onClose={() => setDetailAugmentation(null)}
        />
      )}
    </div>
  );
}
